import * as React from "react";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { JSX } from "react";
import Features from "@app/feature-flags/features";
import { ViewableFieldsService } from "@app/viewable-fields/viewableFieldsService";
import { TableType, ViewableField } from "@app/viewable-fields/viewableFields";
import { ViewableFieldsContextProps, ViewableFieldsContext } from "@app/viewable-fields/ViewableFieldsContext";

const viewableFieldsService = new ViewableFieldsService();

interface ViewableFieldsContainerProps {
  children: React.ReactNode;
  projectRunConfigurationId: number;
  customProjectId: number;
  specialtyGroupCode: string;
}

/**
 * Manages the lifecycle of information surrounding viewable fields.
 * @param props The props used to initialize. Specifically, the resource that is currently scoped.
 */
function ViewableFieldsContainer(props: ViewableFieldsContainerProps): JSX.Element {
  const [viewableFieldsByTableType, setViewableFieldsByTableType] = React.useState(new Map<TableType, ViewableField[]>());
  const projectRunConfigurationId = props?.projectRunConfigurationId;
  const featureFlagContext = React.useContext(FeatureFlagContext);

  const setByTableType = (tableType: TableType, viewableFields: ViewableField[]) => {
    const newMap = new Map(viewableFieldsByTableType);
    newMap.set(tableType, viewableFields);
    setViewableFieldsByTableType((oldMap) => new Map([...oldMap, ...newMap]));
  };

  const getByTableType = (tableType: TableType) => {
    getViewableFields(props.customProjectId, props.projectRunConfigurationId, props.specialtyGroupCode, tableType).then((data) => { setByTableType(tableType, data); });
  };

  // DAH: The whole update process seems off.
  const updateByTableType = async (tableType: TableType, viewableFields: ViewableField[]) => {
    await viewableFieldsService.saveViewableFields(props.customProjectId, props.projectRunConfigurationId, props.specialtyGroupCode, tableType, viewableFields);
    const fields = await getViewableFields(props.customProjectId, props.projectRunConfigurationId, props.specialtyGroupCode, tableType);
    setByTableType(tableType, fields);
  };

  const resetByTableType = async (tableType: TableType) => {
    await viewableFieldsService.resetViewableFields(props.customProjectId, props.projectRunConfigurationId, props.specialtyGroupCode, tableType);
    const fields = await getViewableFields(props.customProjectId, props.projectRunConfigurationId, props.specialtyGroupCode, tableType);
    setByTableType(tableType, fields);
  };

  const getAllViewableFields = (tableTypes: TableType[]) => {
    const newFields = new Map(viewableFieldsByTableType);
    const apiCalls = [];

    for (const tt of tableTypes) {
      apiCalls.push(viewableFieldsService.getViewableFieldsByCustomProject(props.customProjectId, props.projectRunConfigurationId, props.specialtyGroupCode, tt));
    }
    Promise.all(apiCalls).then((response) => {
      tableTypes.forEach((tt, index) => {
        let fields = response[index];
        if (!featureFlagContext.enabledFeatureFlags.has(Features.SSA_TO_FIPS)) {
          fields = filterOutCountyFIPS(fields);
        }
        newFields.set(tt, fields);
      });
      setViewableFieldsByTableType((oldFields) => new Map([...oldFields, ...newFields]));
    });
  };

  const getViewableFields = async (customProjectId: number, runConfigurationId: number, specialtyGroupCode: string, tableType: TableType): Promise<ViewableField[]> => {
    let fields = await viewableFieldsService.getViewableFieldsByCustomProject(props.customProjectId, props.projectRunConfigurationId, props.specialtyGroupCode, tableType)
    if (!featureFlagContext.enabledFeatureFlags.has(Features.SSA_TO_FIPS)) {
      fields = filterOutCountyFIPS(fields);
    }
    return fields;
  };

  const filterOutCountyFIPS = (fields: ViewableField[]): ViewableField[] => fields.filter(x => x.backingFieldName !== "CountyFIPS");

  const providedValue = React.useMemo<ViewableFieldsContextProps>(() => ({
    getAllViewableFields,
    getByTableType,
    projectRunConfigurationId,
    resetByTableType,
    updateByTableType,
    viewableFieldsByTableType
  }), [viewableFieldsByTableType, projectRunConfigurationId]);

  return (
    <ViewableFieldsContext.Provider value={providedValue}>
      {props.children}
    </ViewableFieldsContext.Provider>
  );
}

export { ViewableFieldsContainer };
